import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Divider,
  Button,
} from "@mui/material";
import dayjs from "dayjs";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import useFirestore from "../../../../config/fireBaseHook"; // Updated path and hook name
import { useAuth } from "../../../../config/AuthContext";
import { storage } from "../../../../config/firebase";
import { normalizeIrn } from "../../../../utils/irn";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

// Format a Firestore Timestamp, ISO string, or Date as MM-DD-YYYY
const formatDateMMDDYYYY = (value) => {
  if (!value) return "";
  const date = value.toDate ? value.toDate() : value;
  const formatted = dayjs(date).format("MM-DD-YYYY");
  return formatted === "Invalid Date" ? "" : formatted;
};

// Blueprint shell_name formatting change takes effect for shells created on
// or after this date (CONVERT(DATETIME, '20260806', 112) in the formula).
const BLUEPRINT_CUTOFF = new Date(2026, 7, 6);

// Tartan ID is always 7 digits — pad with leading zeros if shorter.
const padTartanId = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return trimmed;
  return trimmed.padStart(7, "0");
};

const Edit = ({ open, onClose, course, onRecordUpdated }) => {
  const { updateData } = useFirestore("TpCourseShell"); // Fixed method name

  // Hooks for fetching dropdown data
  const { data: creditCaptureStatusData } = useFirestore("CreditCaptureStatus");
  const { data: fpsData } = useFirestore("Fps");
  const { data: creditTypeData } = useFirestore("TpCreditType");
  const { data: courseData } = useFirestore("TpCourseCatalog");
  const { data: courseShellStatusData } = useFirestore("CourseShellStatus");
  const { data: shellRequestStatusData } = useFirestore("ShellRequestStatus");
  const { data: sitesData } = useFirestore("Sites");
  const { data: partnersData } = useFirestore("TpPartners");
  const { data: usersData } = useFirestore("users");
  const { data: elearnShellTypeData } = useFirestore("ElearnShellType");
  const { data: pathwayData } = useFirestore("PathwaysPrograms");
  const { user } = useAuth();

  const [formData, setFormData] = useState(course || {}); // Handle undefined course
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  // Files picked for shell_roster_file/credential_upload_A/B/C, keyed by
  // field name — held locally and only actually uploaded to Storage inside
  // handleSubmit, since this form has one combined Save button rather than
  // an upload-on-select flow.
  const [pendingFiles, setPendingFiles] = useState({});

  const handleFilePick = (fieldName) => (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPendingFiles((prev) => ({ ...prev, [fieldName]: file }));
    }
  };

  // Helper function to safely get field values
  const getFieldValue = (fieldName) => {
    return formData[fieldName] || "";
  };

  useEffect(() => {
    setFormData({
      ...course,
      // Default to "Not Started" when the record has no credit capture status yet
      credit_capture_status: course?.credit_capture_status || "Not Started",
    });
    setPendingFiles({});
  }, [course]);

  // Self-heal a stale cached tp_site_of_delivery/tp_partner name (and the
  // shell_name text that embeds them) whenever this record is opened,
  // instead of only refreshing it if the user happens to reopen that
  // specific dropdown. Resolves from the record's stable sod_irn/
  // tp_partner_irn against the live Sites/TpPartners data, so a rename
  // doesn't leave this record showing the old name forever. Merges into
  // the current formData rather than resetting it, and only applies if
  // the user hasn't already changed that field away from the record's
  // original value this session, so it can't clobber an in-progress edit.
  useEffect(() => {
    if (!course) return;
    const resolvedSite = (sitesData || []).find(
      (s) => normalizeIrn(s.SiteID) === normalizeIrn(course.sod_irn),
    );
    const resolvedPartner = (partnersData || []).find(
      (p) =>
        normalizeIrn(p.PartnerIRN) === normalizeIrn(course.tp_partner_irn),
    );
    if (!resolvedSite && !resolvedPartner) return;

    setFormData((prev) => {
      const updated = { ...prev };
      let changed = false;
      if (resolvedSite && prev.sod_irn === course.sod_irn) {
        updated.tp_site_of_delivery = resolvedSite.SiteNameShort;
        // Snap to the canonical padded SiteID (e.g. "19407" -> "019407")
        // so the Select's value actually matches a rendered MenuItem.
        updated.sod_irn = resolvedSite.SiteID;
        changed = true;
      }
      if (resolvedPartner && prev.tp_partner_irn === course.tp_partner_irn) {
        updated.tp_partner = resolvedPartner.Tech_Prep_Partner;
        // Snap to the canonical padded PartnerIRN for the same reason.
        updated.tp_partner_irn = resolvedPartner.PartnerIRN;
        changed = true;
      }
      if (!changed) return prev;
      updated.shell_name = calculateShellName(updated);
      return updated;
    });
  }, [course, sitesData, partnersData]);

  // Blueprint course shells created on/after 2026-08-06 use the new "BP - "
  // naming (CONVERT(DATETIME, '20260806', 112) in the source formula).
  const resolveTimeCreated = (data) => {
    const value = data.time_created;
    if (value) {
      if (typeof value.toDate === "function") return value.toDate(); // Firestore Timestamp
      if (value instanceof Date) return value;
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  };

  // Helper function to calculate shell_name
  const calculateShellName = (data) => {
    const {
      shell_type,
      course,
      subject,
      tp_section_number,
      shell_school_year_start,
      tp_site_of_delivery,
      credit_type,
    } = data;

    const courseStr = course || "";

    if (shell_type === "Master") {
      return `${courseStr} ${subject} (${tp_section_number}) - Tech Prep Master`;
    }

    if (
      shell_type === "Blueprint" &&
      resolveTimeCreated(data) >= BLUEPRINT_CUTOFF
    ) {
      const courseLeft3 = courseStr.substring(0, 3);
      const courseRight4 = courseStr.substring(courseStr.length - 4);
      return `BP - ${courseLeft3} ${courseRight4} ${subject} (${tp_section_number}) - TechPrep`;
    }

    if (courseStr === "CLT-1113 & CLT-1114") {
      return `${shell_school_year_start}.CLT.1113.&.CLT.1114.${tp_section_number} - ${subject} (${tp_site_of_delivery})`;
    }

    if (shell_type === "Regular" && credit_type === "ITAG") {
      const courseNoDots = courseStr.replace(/\./g, "");
      const digitIdx = courseNoDots.search(/[0-9]/);
      if (digitIdx > -1) {
        const prefix = courseNoDots.substring(0, digitIdx);
        const suffix = courseNoDots.substring(digitIdx);
        return `${shell_school_year_start}.${prefix}.${suffix}.${tp_section_number} - ${subject} (${tp_site_of_delivery})`;
      }
      return `${shell_school_year_start}.${courseNoDots}.${tp_section_number} - ${subject} (${tp_site_of_delivery})`;
    }

    const courseLeft3 = courseStr.substring(0, 3);
    const courseRight4 = courseStr.substring(courseStr.length - 4);
    return `${shell_school_year_start}.${courseLeft3}.${courseRight4}.${tp_section_number} - ${subject} (${tp_site_of_delivery})`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => {
      const newData = {
        ...prevData,
        [name]: value || "", // Ensure empty string instead of undefined
      };

      // Auto-calculate shell_name based on the formula for relevant fields
      if (
        [
          "shell_type",
          "course",
          "subject",
          "tp_section_number",
          "shell_school_year_start",
          "tp_site_of_delivery",
          "credit_type",
        ].includes(name)
      ) {
        newData.shell_name = calculateShellName(newData);
      }

      return newData;
    });
  };

  // Handle course selection and auto-populate subject
  const handleCourseChange = (e) => {
    const { value } = e.target;
    const selectedCourse = courseData.find((course) => course.Course === value);

    setFormData((prevData) => {
      const newData = {
        ...prevData,
        course: value,
        subject: selectedCourse ? selectedCourse.Subject || "" : "",
      };

      // Calculate shell_name with new data
      newData.shell_name = calculateShellName(newData);

      return newData;
    });
  };

  // Handle TP Partner selection and auto-populate TP Partner IRN. Keyed by
  // PartnerIRN (stable) rather than Tech_Prep_Partner (freely renamable) so
  // a later rename in TpPartners can't disconnect this selection.
  const handleTpPartnerChange = (e) => {
    const { value } = e.target;
    const selectedPartner = partnersData?.find(
      (partner) => partner.PartnerIRN === value,
    );

    setFormData((prevData) => ({
      ...prevData,
      tp_partner: selectedPartner?.Tech_Prep_Partner || "",
      tp_partner_irn: value,
    }));
  };

  // Handle TP Site of Delivery selection and auto-populate SOD IRN. Keyed
  // by SiteID (stable) rather than SiteNameShort (freely renamable) so a
  // later rename in TpSites can't disconnect this selection.
  const handleTpSiteOfDeliveryChange = (e) => {
    const { value } = e.target;
    const selectedSite = sitesData?.find((site) => site.SiteID === value);

    setFormData((prevData) => {
      const newData = {
        ...prevData,
        tp_site_of_delivery: selectedSite?.SiteNameShort || "",
        sod_irn: value,
      };

      // Calculate shell_name with new data
      newData.shell_name = calculateShellName(newData);

      return newData;
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked ? -1 : 0,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Assuming 'course.id' is the Firestore document ID.
      // The 'updateData' hook function should handle updating the document
      // with the given ID using the formData.
      if (!course || !course.id) {
        throw new Error("Course ID is missing, cannot update.");
      }

      // Create a copy of formData to potentially modify
      const submitData = { ...formData };

      // If TP Final Approval is being checked and user is logged in, set authorization name
      // Only update if approval is checked AND the current data doesn't already have approval checked
      if (submitData.tp_final_approval === -1 && user?.name) {
        // If this is newly checked (wasn't checked before), set the authorization name
        if (course.tp_final_approval !== -1) {
          submitData.tp_final_authorization_name = user.name;
          submitData.time_approved = new Date();
        }
      }

      // Upload any newly-picked roster/credential files to Storage and swap
      // in their download URLs before saving, so the upload happens inside
      // this same save action rather than a separate round-trip.
      const fileFields = Object.keys(pendingFiles);
      if (fileFields.length > 0) {
        await Promise.all(
          fileFields.map(async (fieldName) => {
            const file = pendingFiles[fieldName];
            const storageRef = ref(
              storage,
              `courseshell/${course.id}/${fieldName}_${file.name}`,
            );
            await uploadBytes(storageRef, file);
            submitData[fieldName] = await getDownloadURL(storageRef);
          }),
        );
      }

      // credentials_uploaded was a dead, always-blank display field —
      // make it a simple live summary of the 3 credential slots instead.
      const uploadedCredentialCount = ["A", "B", "C"].filter(
        (slot) => submitData[`credential_upload_${slot}`],
      ).length;
      submitData.credentials_uploaded = `${uploadedCredentialCount} of 3`;

      await updateData(course.id, submitData); // Fixed method name
      setPendingFiles({});
      setLoading(false);
      setSnackbarOpen(true);
      onRecordUpdated();
    } catch (error) {
      console.error("Error updating record:", error);
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // The X (and backdrop/Escape, which share the same Dialog onClose) always
  // asks for confirmation first rather than closing immediately, so an
  // in-progress edit isn't lost by an accidental click.
  const handleCloseAttempt = () => {
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseAttempt} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleCloseAttempt}>
          Edit Record
        </ModalDialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {/* ── Top: Shell Name (full width) ───────────────────────────── */}
          <Grid item xs={12}>
            <TextField
              label="Shell Name"
              name="shell_name"
              value={getFieldValue("shell_name")}
              disabled
              fullWidth
              margin="normal"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#999",
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel>TP Partner</InputLabel>
              <Select
                name="tp_partner"
                value={formData.tp_partner_irn || ""}
                onChange={handleTpPartnerChange}
                label="TP Partner"
              >
                {partnersData
                  ?.filter(
                    (partner) =>
                      partner.Active_yn === "y" ||
                      partner.PartnerIRN === formData.tp_partner_irn,
                  )
                  .sort((a, b) =>
                    (a.Tech_Prep_Partner || "").localeCompare(
                      b.Tech_Prep_Partner || "",
                    ),
                  )
                  .map((partner) => (
                    <MenuItem key={partner.id} value={partner.PartnerIRN}>
                      {partner.Tech_Prep_Partner}
                      {partner.Active_yn !== "y" ? " (Inactive)" : ""}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel>TP Site of Delivery</InputLabel>
              <Select
                name="tp_site_of_delivery"
                value={formData.sod_irn || ""}
                onChange={handleTpSiteOfDeliveryChange}
                label="TP Site of Delivery"
              >
                {sitesData
                  ?.sort((a, b) =>
                    (a.SiteNameShort || "").localeCompare(
                      b.SiteNameShort || "",
                    ),
                  )
                  .map((site) => (
                    <MenuItem key={site.id} value={site.SiteID}>
                      {site.SiteNameShort}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Primary TP Instructor</InputLabel>
              <Select
                name="primary_tp_inst_ID"
                value={formData.primary_tp_inst_ID || ""}
                onChange={handleChange}
                label="Primary TP Instructor"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {usersData
                  ?.filter((user) => user.userTypes?.instructor === true)
                  .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                  .map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Secondary TP Instructor</InputLabel>
              <Select
                name="secondary_tp_inst_ID"
                value={formData.secondary_tp_inst_ID || ""}
                onChange={handleChange}
                label="Secondary TP Instructor"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {usersData
                  ?.filter((user) => user.userTypes?.instructor === true)
                  .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                  .map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel>TP Pathway Manager</InputLabel>
              <Select
                name="tp_pathway_manager_ID"
                value={formData.tp_pathway_manager_ID || ""}
                onChange={handleChange}
                label="TP Pathway Manager"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {usersData
                  ?.filter((user) => user.userTypes?.pathwayManager === true)
                  .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                  .map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Credit Capture Status</InputLabel>
              <Select
                name="credit_capture_status"
                value={formData.credit_capture_status || ""}
                onChange={handleChange}
                label="Credit Capture Status"
              >
                {creditCaptureStatusData
                  .sort((a, b) =>
                    (a.Credit_Capture_Status || "").localeCompare(
                      b.Credit_Capture_Status || "",
                    ),
                  )
                  .map((status) => (
                    <MenuItem
                      key={status.id}
                      value={status.Credit_Capture_Status}
                    >
                      {status.Credit_Capture_Status}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Credentials Uploaded"
              name="credentials_uploaded"
              value={formData.credentials_uploaded || ""}
              fullWidth
              margin="normal"
              disabled
              helperText="Auto-calculated from the 3 credential uploads below"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#999",
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>
          {["A", "B", "C"].map((slot) => {
            const fieldName = `credential_upload_${slot}`;
            return (
              <Grid item xs={12} sm={4} key={fieldName}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 2 }}
                >
                  Credential Upload {slot}
                </Typography>
                {formData[fieldName] && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <a
                      href={formData[fieldName]}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View current file
                    </a>
                  </Typography>
                )}
                <input type="file" onChange={handleFilePick(fieldName)} />
                {pendingFiles[fieldName] && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    Selected: {pendingFiles[fieldName].name} (uploads on Save)
                  </Typography>
                )}
              </Grid>
            );
          })}
          <Grid item xs={12} sm={8}>
            <TextField
              label="Credit Capture Status Note"
              name="credit_capture_status_note"
              value={formData.credit_capture_status_note}
              onChange={handleChange}
              fullWidth
              margin="normal"
              multiline
              rows={4}
            />
          </Grid>

          {/* ── Middle: Shell / Course Details ─────────────────────────── */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Shell &amp; Course Details
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Shell Type</InputLabel>
              <Select
                name="shell_type"
                value={formData.shell_type || ""}
                onChange={handleChange}
                label="Shell Type"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {elearnShellTypeData
                  ?.sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                  .map((shellType) => (
                    <MenuItem key={shellType.id} value={shellType.name}>
                      {shellType.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Credit Type</InputLabel>
              <Select
                name="credit_type"
                value={formData.credit_type || ""}
                onChange={handleChange}
                label="Credit Type"
              >
                {creditTypeData
                  .sort((a, b) =>
                    (a.Credit_Type_Name || "").localeCompare(
                      b.Credit_Type_Name || "",
                    ),
                  )
                  .map((type) => (
                    <MenuItem key={type.id} value={type.Credit_Type_abv}>
                      {type.Credit_Type_Name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel>TP Term</InputLabel>
              <Select
                name="tp_term"
                value={formData.tp_term || ""}
                onChange={handleChange}
                label="TP Term"
              >
                <MenuItem value="All Year">All Year</MenuItem>
                <MenuItem value="Fall">Fall</MenuItem>
                <MenuItem value="Spring">Spring</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Course</InputLabel>
              <Select
                name="course"
                value={formData.course || ""}
                onChange={handleCourseChange}
                label="Course"
              >
                {courseData
                  ?.filter(
                    (course, index, self) =>
                      // Remove duplicates by keeping only the first occurrence of each Course value
                      index ===
                      self.findIndex((c) => c.Course === course.Course),
                  )
                  .sort((a, b) =>
                    (a.Course || "").localeCompare(b.Course || ""),
                  )
                  .map((course) => (
                    <MenuItem key={course.id} value={course.Course}>
                      {course.Course}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              fullWidth
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="TP Section Number"
              name="tp_section_number"
              value={formData.tp_section_number}
              disabled
              fullWidth
              margin="normal"
              helperText="Auto-generated, not editable"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#999",
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Primary FPS</InputLabel>
              <Select
                name="primary_fps_ID"
                value={formData.primary_fps_ID || ""}
                onChange={handleChange}
                label="Primary FPS"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {fpsData
                  ?.filter((fps) => fps.Full_Name && fps.fps_Id)
                  .sort((a, b) =>
                    (a.Full_Name || "").localeCompare(b.Full_Name || ""),
                  )
                  .map((fps) => (
                    <MenuItem key={fps.id} value={fps.fps_Id}>
                      {fps.Full_Name}
                      {fps.Tartan_Id ? ` (${padTartanId(fps.Tartan_Id)})` : ""}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Secondary FPS</InputLabel>
              <Select
                name="secondary_fps_ID"
                value={formData.secondary_fps_ID || ""}
                onChange={handleChange}
                label="Secondary FPS"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {fpsData
                  ?.filter((fps) => fps.Full_Name && fps.fps_Id)
                  .sort((a, b) =>
                    (a.Full_Name || "").localeCompare(b.Full_Name || ""),
                  )
                  .map((fps) => (
                    <MenuItem key={fps.id} value={fps.fps_Id}>
                      {fps.Full_Name}
                      {fps.Tartan_Id ? ` (${padTartanId(fps.Tartan_Id)})` : ""}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Shell School Year Start"
              name="shell_school_year_start"
              value={formData.shell_school_year_start}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Shell School Year End"
              name="shell_school_year_end"
              value={formData.shell_school_year_end}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Pathway Program</InputLabel>
              <Select
                name="tp_pathway_program_ID"
                value={formData.tp_pathway_program_ID || ""}
                onChange={handleChange}
                label="Pathway Program"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {pathwayData
                  ?.sort((a, b) =>
                    (a.PathwayName || "").localeCompare(b.PathwayName || ""),
                  )
                  .map((pathway) => (
                    <MenuItem key={pathway.id} value={pathway.id}>
                      {pathway.PathwayName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel>What Year Taking Course</InputLabel>
              <Select
                name="what_year_taking_course"
                value={formData.what_year_taking_course || ""}
                onChange={handleChange}
                label="What Year Taking Course"
              >
                <MenuItem value="Juniors">Juniors</MenuItem>
                <MenuItem value="Juniors & Seniors">Juniors & Seniors</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
                <MenuItem value="Seniors">Seniors</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Tech Prep Modality"
              name="tech_prep_modality"
              value={formData.tech_prep_modality}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Credit Capture Scheme</InputLabel>
              <Select
                name="credit_capture_scheme"
                value={formData.credit_capture_scheme || ""}
                onChange={handleChange}
                label="Credit Capture Scheme"
              >
                <MenuItem value="AA Scheme">AA Scheme</MenuItem>
                <MenuItem value="FLEX Scheme">FLEX Scheme</MenuItem>
                <MenuItem value="PR Scheme">PR Scheme</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="TP Partner IRN"
              name="tp_partner_irn"
              value={formData.tp_partner_irn || ""}
              fullWidth
              margin="normal"
              disabled
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#999",
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="SOD IRN"
              name="sod_irn"
              value={formData.sod_irn || ""}
              fullWidth
              margin="normal"
              disabled
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#999",
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Note 01"
              name="note_01"
              value={formData.note_01}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />
          </Grid>

          {/* ── Bottom: Approval Details ────────────────────────────────── */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Approval Details
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.tp_final_approval === -1}
                  onChange={handleCheckboxChange}
                  name="tp_final_approval"
                />
              }
              label="TP Final Approval"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="TP Final Authorization Name"
              name="tp_final_authorization_name"
              value={getFieldValue("tp_final_authorization_name")}
              disabled
              fullWidth
              margin="normal"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#999",
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Course Request From"
              name="course_request_from"
              value={formData.course_request_from || ""}
              fullWidth
              margin="normal"
              disabled
              helperText="Set on creation, not editable"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#999",
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Time Approved"
              name="time_approved"
              value={formatDateMMDDYYYY(formData.time_approved)}
              fullWidth
              margin="normal"
              disabled
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#999",
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Time Created"
              name="time_created"
              value={formatDateMMDDYYYY(formData.time_created)}
              fullWidth
              margin="normal"
              disabled
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#999",
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Shell Status</InputLabel>
              <Select
                name="status"
                value={formData.status || ""}
                onChange={handleChange}
                label="Status"
              >
                {shellRequestStatusData
                  ?.sort((a, b) =>
                    (a.Status || "").localeCompare(b.Status || ""),
                  )
                  .map((status) => (
                    <MenuItem key={status.id} value={status.Status}>
                      {status.Status}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 2 }}
            >
              Shell Roster File
            </Typography>
            {formData.shell_roster_file && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <a
                  href={formData.shell_roster_file}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View current file
                </a>
              </Typography>
            )}
            <input type="file" onChange={handleFilePick("shell_roster_file")} />
            {pendingFiles.shell_roster_file && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                Selected: {pendingFiles.shell_roster_file.name} (uploads on
                Save)
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <ModalActions onSave={handleSubmit} saving={loading} />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message="Record has been updated"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        ContentProps={{
          sx: {
            backgroundColor: "black",
            color: "white",
          },
        }}
      />
      </Dialog>
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Edit;
